/**
 * Cross-session staleness → out-of-date banner copy (spec 06-VER, thin wiring only).
 *
 * Maps the server staleness reason enums to human sentences and composes them with the
 * dialog's own in-session edit reason into ONE out-of-date notice — never a second,
 * stacked banner. Unknown / future server enums degrade to a generic sentence rather
 * than leaking a raw key, so a BE that grows a new reason is forward-safe on the client.
 */

import { capitalizeFirstLetter, joinClauses } from 'utils/strings';
import type { StalenessInfo } from './types';

/** The generic fallback for an unrecognized (future) server reason enum. */
export const GENERIC_STALENESS_SENTENCE = 'the scenario configuration changed';

/** Server reason enum → the sentence the banner lists it as (spec §5). */
export const STALENESS_REASON_SENTENCES: Record<string, string> = {
  scenarioEdited: 'the Development Pace Scenario changed',
  typeCurveScenarioEdited: 'the Type Curve Scenario changed',
  geometryEdited: 'a geometry changed',
  priorityReordered: 'the Geometry Priority order changed',
  referenceMonthRolled: 'the reference month rolled',
};

/**
 * The sentence for one server reason enum; an unrecognized value degrades to the
 * generic sentence (never a raw key).
 *
 * @param {string} reason - A server staleness reason enum (possibly a future value).
 * @return {string} The human sentence.
 */
export function stalenessReasonSentence(reason: string): string {
  return STALENESS_REASON_SENTENCES[reason] ?? GENERIC_STALENESS_SENTENCE;
}

/**
 * The server staleness reasons as sentences, or `[]` when not stale — the steady state
 * and never-calculated evals both return `stale: false`, so gating on `stale` is the
 * whole suppression rule (no client logic beyond this). Deduped so repeated or multiple
 * unknown reasons don't repeat copy.
 *
 * @param {StalenessInfo | undefined} staleness - The read's staleness object, if present.
 * @return {string[]} The reason sentences (empty when not stale).
 */
export function serverStalenessSentences(staleness?: StalenessInfo): string[] {
  if (!staleness?.stale) {
    return [];
  }
  return Array.from(new Set(staleness.reasons.map(stalenessReasonSentence)));
}

/**
 * The single out-of-date banner subtitle for the composed cause list (server staleness
 * reasons followed by the in-session edit clause). Sentence-cases the joined phrase and
 * appends the unchanged re-run prompt.
 *
 * @param {string[]} clauses - The composed cause clauses; empty ⇒ '' (caller gates on non-empty).
 * @return {string} The banner subtitle.
 */
export function staleBannerSubtitle(clauses: string[]): string {
  if (clauses.length === 0) {
    return '';
  }
  const phrase = joinClauses(clauses);
  return `${capitalizeFirstLetter(phrase)}. Re-run to update.`;
}
